/**
 * Default quick-mark duration of a project in minutes, or null. A value that is not a positive
 * integer reads as null instead of rejecting the record (data dictionary §3,
 * project.quickDurationMin). The upper bound belongs to the caller, which knows the minimum
 * duration, the time step and the day boundary.
 */
export function readQuickDurationMin(data: { readonly quickDurationMin?: unknown }): number | null {
  const value = data.quickDurationMin;
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/** Longest quick-mark block in minutes (8 hours); also the last value the settings select offers. */
export const MAX_QUICK_DURATION_MIN = 480;

/** The values the project settings offer for the quick-mark duration, before the step and minimum filter. */
export const QUICK_DURATION_CHOICES: readonly number[] = [
  5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 300, 360, 480,
];

/** Settings fields the quick-mark duration depends on (`Settings` satisfies it). */
export interface QuickDurationSettings {
  readonly timeStepMin: number;
  readonly minDurationMin: number;
  readonly defaultDurationMin?: number;
}

/**
 * Block length created by a quick mark of a project. With a valid per-project value: the value
 * snapped up to the time step, then capped at `MAX_QUICK_DURATION_MIN`, then raised to the
 * minimum duration - the minimum wins when it exceeds the cap. Without one: the plain default
 * block duration, unchanged (no snapping, no cap), exactly what a click creates without a quick
 * mark. Normalised on read; the stored value is never rewritten when the step or minimum
 * duration change later (data dictionary §3, project.quickDurationMin).
 */
export function effectiveQuickDurationMin(
  data: { readonly quickDurationMin?: unknown },
  settings: Required<QuickDurationSettings>,
): number {
  const own = readQuickDurationMin(data);
  if (own === null) return settings.defaultDurationMin;
  const snapped = Math.ceil(own / settings.timeStepMin) * settings.timeStepMin;
  return Math.max(Math.min(snapped, MAX_QUICK_DURATION_MIN), settings.minDurationMin);
}

/**
 * Choices of the settings select under the current precision: multiples of the step, not below
 * the minimum. Never empty: when the minimum exceeds every listed value, the minimum itself
 * (snapped up to the step) is the one choice.
 */
export function quickDurationChoicesFor(settings: QuickDurationSettings): number[] {
  const choices = QUICK_DURATION_CHOICES.filter(
    (choice) => choice % settings.timeStepMin === 0 && choice >= settings.minDurationMin,
  );
  if (choices.length > 0) return choices;
  return [Math.ceil(settings.minDurationMin / settings.timeStepMin) * settings.timeStepMin];
}
